import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { CheckSquare, ArrowSquareOut, User, Calendar, Flag, Briefcase, Sparkle, Note as NoteIcon, PencilSimple, WarningCircle, Clock } from "@phosphor-icons/react";
import { toast } from "sonner";
import ContactPicker from "@/components/ContactPicker";
import { TASK_PRIORITY_LABEL } from "@/lib/enumLabels";

const STATUS_TONE = {
  done: "text-emerald-500",
  in_progress: "text-primary",
  blocked: "text-destructive",
  todo: "text-muted-foreground",
};

/**
 * Quick-look modal for a task. Renders title, description, owner, status,
 * priority, and due-date in serif hierarchy plus a jump-out to the full
 * Tasks workspace where the partner can edit inline.
 *
 * Iter 85.3 — adds a ContactPicker so a follow-up call can be bound to
 * the right person directly from the Tasks board. Loads the parent
 * Client's contacts (via task.client_id, or engagement→client if the
 * task lives on an engagement) and PUTs `contact_ids` to /tasks/{id}.
 */
export default function TaskQuickViewDialog({
  task, open, onOpenChange, onTaskChange,
  // Iter 85.76 · Optional lookup maps + Edit hook. When the dialog is
  // opened from the Tasks board, callers pass `engagements`, `users`,
  // and `onEdit` so the Quick View resolves canonical labels without
  // secondary fetches and offers an explicit "Edit" affordance. When
  // opened from Client/Engagement detail pages, these can be omitted
  // — the dialog degrades gracefully to its historical shape.
  engagements = [], users = [], onEdit = null,
}) {
  const [contacts, setContacts] = useState([]);
  const [contactIds, setContactIds] = useState([]);
  const [saving, setSaving] = useState(false);
  const [clientId, setClientId] = useState(null);
  // Iter 85.76 · Provenance side-loads. Fetched lazily on open so a
  // partner clicking through a busy board doesn't pay a network fee
  // for every card that never opens the Quick View.
  const [sourceNote, setSourceNote] = useState(null);
  const [activityBriefing, setActivityBriefing] = useState(null);
  useEffect(() => {
    if (!task || !open) { setSourceNote(null); setActivityBriefing(null); return; }
    let alive = true;
    const load = async (nid, setter) => {
      if (!nid) { setter(null); return; }
      try {
        const { data } = await api.get(`/notes/${nid}`);
        if (alive) setter(data);
      } catch { if (alive) setter(null); }
    };
    load(task.source_note_id, setSourceNote);
    load(task.activity_briefing_id, setActivityBriefing);
    return () => { alive = false; };
  }, [task?.id, open, task?.source_note_id, task?.activity_briefing_id]);

  useEffect(() => {
    if (!task || !open) return;
    setContactIds(task.contact_ids || []);
    let cancelled = false;
    (async () => {
      let cid = task.client_id;
      // Task is engagement-scoped without a direct client_id? Resolve.
      if (!cid && task.engagement_id) {
        try {
          const { data } = await api.get(`/engagements/${task.engagement_id}`);
          cid = data?.client_id;
        } catch { /* silent */ }
      }
      if (!cid) {
        if (!cancelled) { setContacts([]); setClientId(null); }
        return;
      }
      try {
        const { data } = await api.get(`/clients/${cid}`);
        if (!cancelled) {
          setContacts(data?.stakeholders || []);
          setClientId(cid);
        }
      } catch {
        if (!cancelled) { setContacts([]); setClientId(cid); }
      }
    })();
    return () => { cancelled = true; };
  }, [task, open]);

  if (!task) return null;
  const dueLabel = task.due_date
    ? new Date(task.due_date).toLocaleDateString(undefined, { weekday: "short", month: "long", day: "numeric" })
    : null;
  // Iter 85.76 · Urgency chips mirror the Tasks-board rail so the Quick
  // View feels visually consistent with the card that opened it.
  const todayISO = new Date().toISOString().slice(0, 10);
  const soonISO = (() => { const d = new Date(); d.setDate(d.getDate() + 7); return d.toISOString().slice(0, 10); })();
  const overdueChip = task.due_date && task.due_date < todayISO && task.status !== "done";
  const dueSoonChip = task.due_date && !overdueChip && task.due_date <= soonISO && task.status !== "done";
  const engagement = engagements.find((e) => e.id === task.engagement_id) || null;

  const saveContacts = async (ids) => {
    setContactIds(ids);
    setSaving(true);
    try {
      const { data } = await api.put(`/tasks/${task.id}`, { contact_ids: ids });
      onTaskChange?.(data);
      toast.success("Contacts updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none bg-card max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="task-quickview-dialog">
        <DialogHeader className="border-b border-primary/30 pb-4">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="text-[0.625rem] font-mono uppercase tracking-[0.2em] text-primary flex items-center gap-1.5">
                <CheckSquare size={11} weight="fill" /> Task
                <span data-testid="task-quickview-status" className={`ml-2 ${STATUS_TONE[task.status] || "text-muted-foreground"}`}>{task.status}</span>
              </div>
              <DialogTitle data-testid="task-quickview-title" className="font-display text-2xl tracking-tight">{task.title}</DialogTitle>
              <DialogDescription className="sr-only">Task quick view — read-only details plus an explicit Edit action.</DialogDescription>
              <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground flex flex-wrap gap-x-4 gap-y-1 mt-1">
                {(() => {
                  const assignee = users.find((u) => u.id === task.assignee_id);
                  const owner = task.owner_name || assignee?.name;
                  return owner && <span data-testid="task-quickview-assignee" className="flex items-center gap-1"><User size={11} /> {owner}</span>;
                })()}
                {task.priority && <span data-testid="task-quickview-priority" className="flex items-center gap-1"><Flag size={11} /> {TASK_PRIORITY_LABEL[task.priority] || task.priority}</span>}
                {dueLabel && (
                  <span
                    data-testid={overdueChip ? "task-quickview-overdue" : (dueSoonChip ? "task-quickview-duesoon" : "task-quickview-due")}
                    className={`flex items-center gap-1 ${overdueChip ? "text-destructive" : (dueSoonChip ? "text-amber-500" : "")}`}
                  >
                    {overdueChip ? <WarningCircle size={11} weight="fill" /> : (dueSoonChip ? <Clock size={11} weight="bold" /> : <Calendar size={11} />)}
                    due {dueLabel}
                  </span>
                )}
              </div>
            </div>
            {onEdit && (
              <Button
                data-testid="task-quickview-edit"
                size="sm"
                onClick={() => onEdit(task)}
                className="rounded-none bg-primary text-primary-foreground shrink-0"
              >
                <PencilSimple size={12} weight="bold" className="mr-1" /> Edit
              </Button>
            )}
          </div>
        </DialogHeader>

        {task.description && (
          <section data-testid="task-quickview-description">
            <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground mb-2">Description</div>
            <div className="font-serif text-sm leading-relaxed whitespace-pre-wrap">{task.description}</div>
          </section>
        )}

        {/* Iter 85.76 · Engagement + Provenance side-panel. */}
        {(engagement || sourceNote || activityBriefing) && (
          <section className="space-y-2" data-testid="task-quickview-context">
            {engagement && (
              <div className="flex items-start gap-2 text-sm" data-testid="task-quickview-engagement">
                <span className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground w-24 pt-0.5">Engagement</span>
                <Link
                  to={`/engagements/${engagement.id}`}
                  className="inline-flex items-center gap-1 text-primary hover:underline"
                  onClick={() => onOpenChange?.(false)}
                >
                  <Briefcase size={12} weight="bold" /> {engagement.name} <ArrowSquareOut size={10} weight="bold" />
                </Link>
              </div>
            )}
            {activityBriefing && (
              <div className="flex items-start gap-2 text-sm" data-testid="task-quickview-activity">
                <span className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground w-24 pt-0.5">Activity</span>
                <Link
                  to={`/notes?open=${activityBriefing.id}`}
                  className="inline-flex items-center gap-1 text-primary hover:underline"
                  onClick={() => onOpenChange?.(false)}
                >
                  <Sparkle size={12} weight="bold" /> {activityBriefing.title || "Activity briefing"} <ArrowSquareOut size={10} weight="bold" />
                </Link>
              </div>
            )}
            {sourceNote && (!activityBriefing || sourceNote.id !== activityBriefing.id) && (
              <div className="flex items-start gap-2 text-sm" data-testid="task-quickview-source-note">
                <span className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground w-24 pt-0.5">Source</span>
                <Link
                  to={`/notes?open=${sourceNote.id}`}
                  className="inline-flex items-center gap-1 text-primary hover:underline"
                  onClick={() => onOpenChange?.(false)}
                >
                  <NoteIcon size={12} weight="bold" /> {sourceNote.title || "Source note"} <ArrowSquareOut size={10} weight="bold" />
                </Link>
              </div>
            )}
          </section>
        )}

        {/* Iter 85.3 · Bind this task to real people so touch cadence updates. */}
        {clientId && (
          <section>
            <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground mb-2">
              Contacts {saving && <span className="text-primary/70">· saving…</span>}
            </div>
            <ContactPicker
              contacts={contacts}
              value={contactIds}
              onChange={saveContacts}
              testidPrefix="task-contact-picker"
            />
          </section>
        )}

        {(task.checklist || []).length > 0 && (
          <section>
            <div className="text-[0.625rem] font-mono uppercase tracking-widest text-primary mb-2">Checklist ({task.checklist.length})</div>
            <ul className="space-y-1.5 text-sm">
              {task.checklist.map((c, i) => (
                <li key={i} className={c.done ? "line-through text-muted-foreground" : ""}>
                  · {c.text || c}
                </li>
              ))}
            </ul>
          </section>
        )}

        <DialogFooter className="border-t border-border pt-4">
          <Link
            to={`/tasks?open=${task.id}`}
            className="text-primary hover:opacity-80 inline-flex items-center gap-1 text-xs font-mono uppercase tracking-widest"
            data-testid="task-quickview-open-workspace"
          >
            Open in Tasks workspace <ArrowSquareOut size={12} />
          </Link>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
